import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useParams, useNavigate, NavLink, Outlet, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { listGasStations, getAddress } from '@vn-propane/shared/client';
import type { GasStation, ErrorResponse, UserOrganization, Address } from '@vn-propane/shared/client';
import { MapPin, Plus, ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OrganizationContext {
  organization: UserOrganization;
}

interface GasStationContextType {
  station: GasStation | null;
  organization: UserOrganization;
  refreshStations: () => void;
}

const GasStationContext = createContext<GasStationContextType | null>(null);

export function useGasStation() {
  const context = useContext(GasStationContext);
  if (!context) {
    throw new Error('useGasStation must be used within GasStationsLayout');
  }
  return context;
}

export default function GasStationsLayout() {
  const { id: orgId, stationId } = useParams<{ id: string; stationId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OrganizationContext>();
  const [gasStations, setGasStations] = useState<GasStation[]>([]);
  const [addresses, setAddresses] = useState<Record<string, Address>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGasStations = useCallback(async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const result = await listGasStations({ path: { org_id: orgId } });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(errorResponse.error?.message || t('gasStation.failedToLoad'));
        return;
      }

      if (result.data) {
        const stations = result.data.data;
        setGasStations(stations);

        // Fetch addresses for stations that have addressId
        const addressIds = stations
          .map(s => s.addressId)
          .filter((id): id is string => !!id);

        if (addressIds.length > 0) {
          const addressResults = await Promise.all(
            addressIds.map(id => getAddress({ path: { org_id: orgId, id } }))
          );

          const addressMap: Record<string, Address> = {};
          addressResults.forEach(res => {
            if (res.data?.data) {
              addressMap[res.data.data.id] = res.data.data;
            }
          });
          setAddresses(addressMap);
        }
      }
      setError(null);
    } catch (err) {
      setError(t('gasStation.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId, navigate]);

  useEffect(() => {
    fetchGasStations();
  }, [fetchGasStations]);

  const selectedStation = gasStations.find(s => s.id === stationId) || null;

  const formatAddress = (station: GasStation) => {
    if (!station.addressId) return t('gasStation.noAddress');
    const address = addresses[station.addressId];
    return address?.formattedAddress || t('gasStation.noAddress');
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  const tabClass = cn(
    'relative h-10 rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 font-medium text-muted-foreground transition-none',
    'hover:text-foreground'
  );
  const activeTabClass = 'border-primary text-foreground';

  return (
    <GasStationContext.Provider value={{ station: selectedStation, organization, refreshStations: fetchGasStations }}>
      <div className="flex flex-col md:flex-row md:min-h-[calc(100vh-4rem)] -m-4 md:-m-8">
        {/* Left column - Gas Stations List */}
        <div className={cn(
          'md:w-56 flex flex-col border-b md:border-b-0 md:border-r',
          selectedStation && 'hidden md:flex'
        )}>
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">{t('gasStation.title')}</h2>
              <Button size="sm" variant="ghost" asChild>
                <Link to={`/organizations/${orgId}/stations/new`}>
                  <Plus className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {gasStations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <p>{t('gasStation.noGasStationsYet')}</p>
                <Button variant="link" size="sm" asChild>
                  <Link to={`/organizations/${orgId}/stations/new`}>
                    {t('gasStation.addYourFirstStation')}
                  </Link>
                </Button>
              </div>
            ) : (
              <ul className="space-y-1">
                {gasStations.map((station) => (
                  <li key={station.id}>
                    <NavLink
                      to={`/organizations/${orgId}/stations/${station.id}`}
                      className={({ isActive }) => cn(
                        'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between group',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      )}
                    >
                      {({ isActive }) => (
                        <>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{station.name}</div>
                            {station.addressId && addresses[station.addressId] && (
                              <div className={cn(
                                'text-xs truncate flex items-center gap-1',
                                isActive
                                  ? 'text-primary-foreground/70'
                                  : 'text-muted-foreground'
                              )}>
                                <MapPin className="w-3 h-3 shrink-0" />
                                <span className="truncate">{addresses[station.addressId].formattedAddress}</span>
                              </div>
                            )}
                          </div>
                          <ChevronRight className={cn(
                            'w-4 h-4 shrink-0 ml-2',
                            isActive
                              ? 'text-primary-foreground/70'
                              : 'text-muted-foreground opacity-0 group-hover:opacity-100'
                          )} />
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Content area with tabs */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {selectedStation ? (
            <>
              <div className="p-4 md:p-6 pb-0 border-b">
                <button
                  onClick={() => navigate(`/organizations/${orgId}`)}
                  className="md:hidden inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('gasStation.title')}
                </button>
                <h2 className="text-xl md:text-2xl font-extrabold tracking-tight">{selectedStation.name}</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {formatAddress(selectedStation)}
                </p>
              </div>

              <div className="border-b px-4 md:px-6 overflow-x-auto">
                <nav className="flex gap-4 h-10 min-w-max">
                  <NavLink
                    to={`/organizations/${orgId}/stations/${stationId}`}
                    end
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('gasStation.dashboard')}
                  </NavLink>
                  <NavLink
                    to={`/organizations/${orgId}/stations/${stationId}/inventory`}
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('gasStation.inventory')}
                  </NavLink>
                  <NavLink
                    to={`/organizations/${orgId}/stations/${stationId}/deliveries`}
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('gasStation.deliveries')}
                  </NavLink>
                  <NavLink
                    to={`/organizations/${orgId}/stations/${stationId}/settings`}
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('gasStation.settings')}
                  </NavLink>
                </nav>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6">
                <Outlet />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              {t('gasStation.selectGasStation')}
            </div>
          )}
        </div>
      </div>
    </GasStationContext.Provider>
  );
}
